import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, Shield, Globe, Award, Zap, CheckCircle } from 'lucide-react';
import { industries } from '@/data/industries';
import axios from 'axios';
import { SEOHead, buildOrganizationSchema, WEBSITE_SCHEMA } from '@/components/SEOHead';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const API = '/api';

/* ── Reveal ─────────────────────────────────────────────────────── */
function Reveal({ children, delay = 0, className = '', y = 36 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.07 });
  const shouldReduceMotion = useReducedMotion();
  if (shouldReduceMotion) return <div ref={ref} className={className}>{children}</div>;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.85, delay, ease: [0.25, 1, 0.5, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Static data ────────────────────────────────────────────────── */
const differentiators = [
  {
    icon: Shield,
    title: 'Confidentiality First',
    desc: 'Every engagement operates under comprehensive NDAs. Your data, processes, and business relationships are handled with the highest standards of professional discretion.',
  },
  {
    icon: Award,
    title: 'Process Excellence',
    desc: 'Every function we manage is executed through documented, auditable workflows — designed for consistency, quality, and accountability.',
  },
  {
    icon: Globe,
    title: 'International Fluency',
    desc: 'We serve clients across the UK, US, Canada, Europe, Australia, and New Zealand — with the communication standards, time zone discipline, and cultural fluency this requires.',
  },
  {
    icon: Zap,
    title: 'Scalable by Design',
    desc: 'Our operational model scales with your business — up or down — without the overhead, delays, and fixed costs of expanding an internal team.',
  },
];

const caseStudies = [
  {
    industry: 'Healthcare · United Kingdom',
    client: 'United Clinics',
    title: 'Long-Term Operational Support for a UK Healthcare Organisation',
    summary: 'NKP VENTURES provides administrative and operational support for a UK-based healthcare organisation — reducing the administrative burden on clinical staff while maintaining strict confidentiality standards.',
    slug: 'united-clinics-healthcare-operational-support',
  },
  {
    industry: 'Brand Development · E-commerce',
    client: 'HYPPE AROMA',
    title: 'Building a Premium Consumer Brand from Concept to Market',
    summary: 'An in-house brand development project demonstrating end-to-end operational capability — from brand identity and digital infrastructure through to e-commerce execution and ongoing business management.',
    slug: 'hyppe-aroma-brand-development',
  },
];

const SHOW_INDUSTRIES = ['healthcare', 'professional-services', 'smes', 'startups', 'ecommerce-retail', 'financial-services'];

/* ── Component ──────────────────────────────────────────────────── */
export default function Home() {
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [posts, setPosts] = useState([]);
  const s = useSiteSettings();

  useEffect(() => {
    axios.get(`${API}/homepage`).then(r => setHomepageSettings(r.data)).catch(() => {});
    axios.get(`${API}/blog?limit=3`)
      .then(r => {
        const data = r.data;
        setPosts(Array.isArray(data) ? data.slice(0, 3) : (Array.isArray(data?.items) ? data.items.slice(0, 3) : []));
      })
      .catch(() => setPosts([]));
  }, []);

  const hs = homepageSettings;
  const heroLine1 = hs?.hero_heading_line1 || 'Your Business.';
  const heroLine2 = hs?.hero_heading_line2 || 'Our Expertise.';
  const heroSub = hs?.hero_subheading || 'NKP VENTURES provides digital solutions for Indian businesses and reliable outsourcing and operational support for companies across international markets.';

  const displayIndustries = SHOW_INDUSTRIES.map(id => industries.find(ind => ind.id === id)).filter(Boolean);

  return (
    <>
      <SEOHead
        title={s.seo_title_default || 'NKP VENTURES — Digital & Business Solutions for India | Global Outsourcing'}
        description={s.seo_description_default || 'NKP VENTURES provides digital and business solutions for Indian businesses and professional outsourcing support for international markets across the UK, US, Canada, Europe, Australia and New Zealand.'}
        canonical="https://nkpventures.com/"
        structuredData={[buildOrganizationSchema(s), WEBSITE_SCHEMA]}
      />

      {/* ═══════════════════════════════════════════ HERO ══ */}
      <section
        data-testid="home-hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: '#0A1628' }}
      >
        {/* Background ambience */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(ellipse 80% 60% at 110% 40%, rgba(14,90,87,0.16) 0%, transparent 65%), radial-gradient(ellipse 60% 60% at -10% 80%, rgba(21,62,92,0.22) 0%, transparent 65%)',
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.02) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, ease: [0.25, 1, 0.5, 1] }}
            className="max-w-4xl"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2.5 mb-8"
            >
              <span className="w-5 h-px bg-[#D4AF37]" />
              <span className="overline-label text-[#D4AF37]">One Company. Two Markets.</span>
            </motion.div>

            <h1
              data-testid="home-hero-title"
              className="font-serif font-bold leading-[1.05] tracking-tight mb-8"
              style={{ fontSize: 'clamp(2.6rem, 6.5vw, 4.8rem)', color: '#FFFFFF' }}
            >
              {heroLine1}
              <br />
              <span style={{ color: '#D4AF37' }}>{heroLine2}</span>
              <br />
              <span className="text-white/80">Stronger Operations.</span>
            </h1>

            <p className="text-white/60 text-lg leading-relaxed max-w-2xl mb-12 font-sans">
              {heroSub}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                data-testid="home-hero-cta-btn"
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0A1628] font-semibold px-8 py-4 rounded-full transition-all duration-200 text-sm group"
              >
                Let's Build Better Together
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-medium px-8 py-4 rounded-full transition-all duration-200 text-sm"
              >
                Explore Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═════════════════════════ INDIA / GLOBAL CARDS ══ */}
      <section className="py-20 bg-[#F7FAFC]" data-testid="home-market-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <p className="overline-label text-[#0E5A57] mb-3">How Can We Support Your Business?</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628]">
                Two markets. One trusted partner.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* India */}
            <Reveal delay={0.05}>
              <div className="bg-[#0B3F3B] rounded-2xl p-10 flex flex-col h-full min-h-[460px] relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-8">
                    <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-4">India</span>
                    <h3 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-3">
                      Digital & Business Solutions
                    </h3>
                    <p className="text-white/65 text-sm leading-relaxed">
                      Helping Indian businesses build their brand, strengthen their digital presence and streamline their day-to-day operations.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow mb-8">
                    {[
                      { label: 'Digital Solutions', sub: 'Websites, SEO & Digital Marketing' },
                      { label: 'Creative Solutions', sub: 'Branding, Design & Content' },
                      { label: 'Business Support', sub: 'Admin, Back Office & VA' },
                      { label: 'E-Commerce Support', sub: 'Marketplace & Online Operations' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/10 rounded-xl p-4 hover:bg-white/15 transition-colors duration-200">
                        <p className="text-white font-semibold text-sm mb-1">{item.label}</p>
                        <p className="text-white/55 text-xs leading-snug">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/solutions/india"
                    className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0B3F3B] font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200 self-start group"
                  >
                    Explore India Solutions
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* Global */}
            <Reveal delay={0.12}>
              <div className="bg-[#0F2847] rounded-2xl p-10 flex flex-col h-full min-h-[460px] relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-8">
                    <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-4">Global</span>
                    <h3 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-3">
                      Outsourcing & Business Solutions
                    </h3>
                    <p className="text-white/65 text-sm leading-relaxed">
                      Providing reliable outsourcing and operational support to businesses across international markets.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow mb-8">
                    {[
                      { label: 'Business Support', sub: 'Administration & Back Office' },
                      { label: 'Customer Operations', sub: 'Customer & CRM Support' },
                      { label: 'Corporate Functions', sub: 'Finance, Accounting & HR' },
                      { label: 'Specialist Operations', sub: 'Healthcare, E-commerce & Research' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/10 rounded-xl p-4 hover:bg-white/15 transition-colors duration-200">
                        <p className="text-white font-semibold text-sm mb-1">{item.label}</p>
                        <p className="text-white/55 text-xs leading-snug">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/solutions/global"
                    className="inline-flex items-center gap-2 bg-[#0E7B73] hover:bg-[#0F9088] text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200 self-start group"
                  >
                    Explore Global Solutions
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ INDUSTRIES ══ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
              <div>
                <p className="overline-label text-[#0E5A57] mb-3">Industries</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628] leading-tight">
                  Sector experience that matters.
                </h2>
              </div>
              <Link to="/industries" className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:underline shrink-0">
                All Industries <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {displayIndustries.map((ind, i) => (
              <Reveal key={ind.id} delay={i * 0.05}>
                <Link
                  to={`/industries/${ind.slug}`}
                  className="flex flex-col items-center text-center p-5 bg-[#F7FAFC] rounded-2xl border border-[#E5E7EB] hover:border-[#0E5A57]/30 hover:bg-teal-50/40 hover:shadow-sm transition-all duration-250 group"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${ind.color}18` }}>
                    <ind.icon className="w-5 h-5" style={{ color: ind.color }} />
                  </div>
                  <span className="text-[#374151] font-semibold text-xs leading-snug group-hover:text-[#0E5A57] transition-colors duration-200">{ind.shortTitle}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ WHY NKP VENTURES ══ */}
      <section className="py-28 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#D4AF37] mb-4">Why NKP VENTURES</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-16 max-w-2xl leading-tight">
              Not just outsourcing. A genuine operational partnership.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08}>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/8 transition-colors duration-300">
                  <d.icon className="w-7 h-7 text-[#D4AF37] mb-5" />
                  <h3 className="font-serif text-base font-bold text-white mb-3">{d.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors duration-200"
              >
                Learn about NKP VENTURES <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════ CASE EXPERIENCES ══ */}
      <section className="py-28 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
              <div>
                <p className="overline-label text-[#0E5A57] mb-3">Case Experience</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628] leading-tight max-w-lg">
                  Operational partnerships built on trust.
                </h2>
              </div>
              <Link to="/case-experience" className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:underline shrink-0">
                View All Cases <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {caseStudies.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.1}>
                <Link
                  to={`/case-experience/${c.slug}`}
                  className="group flex flex-col p-8 lg:p-10 bg-[#0A1628] rounded-2xl text-white hover:bg-[#0E5A57] transition-colors duration-300 h-full"
                >
                  <div className="overline-label text-teal-400 mb-6">{c.industry}</div>
                  <h3 className="font-serif text-xl lg:text-2xl font-bold mb-4 leading-tight flex-1">{c.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-8">{c.summary}</p>
                  <div className="flex items-center gap-2 text-teal-400 text-sm font-semibold group-hover:gap-3 transition-all duration-200">
                    Read case experience <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════ INSIGHTS ══ */}
      {posts.length > 0 && (
        <section className="py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                <div>
                  <p className="overline-label text-[#0E5A57] mb-3">Insights</p>
                  <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628] leading-tight">
                    Ideas worth reading.
                  </h2>
                </div>
                <Link to="/insights" className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:underline shrink-0">
                  All Insights <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <Reveal key={post.id || post.slug} delay={i * 0.08}>
                  <Link
                    to={`/insights/${post.slug}`}
                    className="group flex flex-col bg-[#F7FAFC] rounded-2xl border border-[#E5E7EB] hover:border-[#0E5A57]/30 hover:shadow-md transition-all duration-300 overflow-hidden h-full"
                  >
                    <div className="p-7 flex flex-col flex-1">
                      <div className="overline-label text-[#0E5A57] mb-4">{post.category || 'Insight'}</div>
                      <h3 className="font-serif text-lg font-bold text-[#0A1628] mb-3 leading-snug group-hover:text-[#0E5A57] transition-colors duration-200 flex-1">{post.title}</h3>
                      <p className="text-[#6B7280] text-sm leading-relaxed line-clamp-2">{post.summary || post.excerpt}</p>
                    </div>
                    <div className="px-7 pb-6 pt-2 border-t border-[#E5E7EB] flex items-center justify-between">
                      <span className="text-xs text-[#9CA3AF]">{post.author || 'NKP VENTURES'}</span>
                      <span className="text-xs text-[#0E5A57] font-semibold group-hover:underline flex items-center gap-1">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════ FINAL CTA ══ */}
      <section className="py-28 bg-[#0B3F3B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <p className="overline-label text-[#D4AF37] mb-4">Get Started</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Let's Build Better Together.
            </h2>
            <p className="text-white/65 text-lg leading-relaxed max-w-xl mx-auto mb-10">
              Whether you need digital solutions in India or reliable operational support for your international business — we're ready to discuss how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0B3F3B] font-semibold px-10 py-4 rounded-full transition-colors duration-200 group"
              >
                Let's Talk
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/80 hover:text-white hover:border-white/50 font-medium px-10 py-4 rounded-full transition-colors duration-200"
              >
                Explore Solutions
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
